import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { db } from '../lib/firebase';
import { collection, addDoc } from 'firebase/firestore';

const Join = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    diploma: '',
    experience: '',
    motivation: '',
    gdprConsent: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const criteria = [
    'Brevet fédéral en assurance privée',
    'Brevet fédéral en assurance sociale',
    'Diplôme de conseiller IAF',
    'Diplôme d\'économiste en assurance ES',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.gdprConsent) {
      setErrorMessage('Veuillez accepter la politique de confidentialité pour continuer.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      await addDoc(collection(db, 'membership_requests'), {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        diploma: formData.diploma,
        experience: formData.experience,
        motivation: formData.motivation || null,
        status: 'pending',
        created_at: new Date()
      });

      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        diploma: '',
        experience: '',
        motivation: '',
        gdprConsent: false,
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setErrorMessage('Une erreur est survenue lors de l\'envoi du formulaire. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  if (submitStatus === 'success') {
    return (
      <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Demande d'adhésion envoyée avec succès !
          </h2>
          <p className="text-gray-600 mb-6">
            Nous avons bien reçu votre demande d'adhésion. Notre équipe l'examinera dans les plus brefs délais et vous contactera par email.
          </p>
          <button
            onClick={() => setSubmitStatus('idle')}
            className="px-6 py-3 bg-dark text-beige rounded-md hover:bg-dark/90 transition-colors"
          >
            Soumettre une nouvelle demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Devenir Membre</h1>

      {/* Critères d'Admission */}
      <section className="mb-12">
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Critères d'Admission</h2>
        <div className="bg-white rounded-lg shadow-lg p-6">
          <ul className="space-y-4">
            {criteria.map((item) => (
              <li key={item} className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Formulaire d'Adhésion */}
      <section>
        <h2 className="text-2xl font-semibold text-gray-900 mb-6">Formulaire d'Adhésion</h2>
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-6">
          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-md">
              {errorMessage}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                Prénom *
              </label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                required
                value={formData.firstName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                Nom *
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                required
                value={formData.lastName}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Téléphone *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                disabled={isSubmitting}
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="diploma" className="block text-sm font-medium text-gray-700">
                Diplôme *
              </label>
              <select
                id="diploma"
                name="diploma"
                required
                value={formData.diploma}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                disabled={isSubmitting}
              >
                <option value="">Sélectionnez votre diplôme</option>
                <option value="brevet-prive">Brevet fédéral en assurance privée</option>
                <option value="brevet-social">Brevet fédéral en assurance sociale</option>
                <option value="conseiller-iaf">Diplôme de conseiller IAF</option>
                <option value="economiste">Diplôme d'économiste en assurance ES</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label htmlFor="experience" className="block text-sm font-medium text-gray-700">
                Expérience professionnelle *
              </label>
              <textarea
                id="experience"
                name="experience"
                required
                value={formData.experience}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                placeholder="Décrivez brièvement votre parcours professionnel..."
                disabled={isSubmitting}
              />
            </div>

            <div className="md:col-span-2">
              <label htmlFor="motivation" className="block text-sm font-medium text-gray-700">
                Motivation
              </label>
              <textarea
                id="motivation"
                name="motivation"
                value={formData.motivation}
                onChange={handleChange}
                rows={3}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-dark focus:ring-dark"
                placeholder="Pourquoi souhaitez-vous rejoindre l'ASDA ?"
                disabled={isSubmitting}
              />
            </div>

            <div className="md:col-span-2">
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="gdprConsent"
                  name="gdprConsent"
                  required
                  checked={formData.gdprConsent}
                  onChange={handleCheckboxChange}
                  className="h-4 w-4 rounded border-gray-300 text-dark focus:ring-dark mt-1"
                  disabled={isSubmitting}
                />
                <label htmlFor="gdprConsent" className="ml-2 block text-sm text-gray-700">
                  J'accepte que mes données personnelles soient traitées conformément à la{' '}
                  <a href="/privacy" className="text-dark hover:text-dark/80 underline">
                    politique de confidentialité
                  </a>
                  . *
                </label>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-6 py-3 border border-transparent text-base font-medium rounded-md text-beige bg-dark hover:bg-dark/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? "Envoi en cours..." : "Envoyer ma demande d'adhésion"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Join;